/* SPEC-462 — the left rail (the "One True SaaS Layout", part 1).
 *
 * A persistent 52px strip of GLOBAL verbs. It is the sanctioned home for icons in the
 * shell: SPEC-456 deleted the per-page row icons because 26 rows shared glyphs and
 * nothing needed them; here a few stable actions each have their own mark AND an
 * accessible name, and they never change by page.
 *
 * Rules (frontend/src/CONTEXT.md, shell invariants):
 *   - Global verbs only. Never a page row, never a per-page icon.
 *   - Which items render is decided by `config/shell` (`railItems`, `railFootItems`,
 *     `brandLinksHome`; unit-tested), from the same role+workspace page list the sidebar
 *     uses — a link here is a page the nav offers too. Admin is hidden, not disabled, for
 *     everyone else.
 *   - The rail stays when the sidebar collapses (the sidebar goes to w-0 and is made
 *     inert), so global navigation is never lost.
 *   - Every control is a real <a>/<button> with an aria-label, a 40px square target
 *     (WCAG 2.5.8 floor is 24px; the spec asks 28px+), a visible focus ring, and a
 *     tooltip that opens on hover AND keyboard focus. The tooltip is aria-hidden: the
 *     aria-label already carries the same words.
 *
 * SPEC-509 — the B marks (components/brand/marks): ink lines plus ONE node. The node is ink
 * at rest (`--mark-accent: currentColor`) and amber when the item is the current page or
 * when something is waiting. Layout, top to bottom: the brand mark (the Overview link), the
 * menu toggle, the verbs, a spacer, the FOOT CLUSTER (Alerts, Walk-throughs, Feedback), then
 * Account. Tab order follows that visual order. */
import React, { useEffect, useRef, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LogOut, KeyRound, ListTree, MonitorOff } from 'lucide-react';
import { useAuth } from '@/lib/auth';
import { useWorkspace } from '@/lib/workspace';
import { useRulings } from '../rulings/RulingsProvider';
import { useTour } from '../tour/TourProvider';
import { SuryaMark } from '../brand/SuryaMark';
import { RailMark } from '../brand/marks/Mark';
import {
  railItems,
  railFootItems,
  brandLinksHome,
  decisionsBadge,
  alertsBadge,
  alertsLabel,
  openFeedback,
  type RailItemId,
  type RailFootItemId,
  type AlertsBadge,
} from '@/config/shell';
import { NAV_GROUP_PAGES } from '@/config/nav-groups';
import { helpHrefFor } from '@/config/docs';
import { useAlertsFeeds } from '@/hooks/useAlertsFeeds';
import { ChangePasswordDialog } from '../account/ChangePasswordForm';
import { useNavLayout } from '@/hooks/useNavLayout';
import { request } from '@/lib/api';

const ITEM_BASE =
  'relative grid place-items-center w-10 h-10 rounded-lg transition-colors ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60';
// At rest the node is ink, like the lines.
const ITEM_IDLE =
  'text-[var(--text-tertiary)] [--mark-accent:currentColor] hover:text-[var(--text-primary)] hover:bg-[var(--sidebar-hover)]';
// The current page: amber lines and node on the active tint.
const ITEM_ACTIVE = 'text-amber-400 [--mark-accent:var(--amber-400)] bg-[var(--sidebar-active-bg)]';
// Something is waiting: ink lines, amber node.
const ITEM_LIT =
  'text-[var(--text-primary)] [--mark-accent:var(--amber-400)] hover:bg-[var(--sidebar-hover)]';

/** Hover/focus tooltip, to the right of the rail. `invisible`, not `opacity-0`: it is
 *  text, and the alpha-on-text guard (check-opacity-text.mjs) forbids faded text. */
function Tip({ text }: { text: string }) {
  return (
    <span
      aria-hidden="true"
      className="pointer-events-none invisible group-hover:visible group-focus-visible:visible absolute left-full top-1/2 -translate-y-1/2 ml-2 z-50 whitespace-nowrap rounded-md border border-[var(--border-default)] bg-[var(--bg-overlay)] px-2 py-1 text-xs font-medium text-[var(--text-primary)] shadow-lg"
    >
      {text}
    </span>
  );
}

/** The Alerts badge: red count, amber dot, or "!" (config/shell.alertsBadge decides which).
 *  aria-hidden — the control's aria-label says the same in words. Shared with the phone rows. */
export function AlertsBadgeMark({ badge, className = 'absolute top-0.5 right-0.5' }: { badge: AlertsBadge; className?: string }) {
  if (!badge) return null;
  if (badge.kind === 'dot') {
    return <span aria-hidden="true" className={`${className} w-2.5 h-2.5 rounded-full bg-amber-400 ring-2 ring-[var(--sidebar-bg)]`} />;
  }
  return (
    <span
      aria-hidden="true"
      className={`${className} min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 text-center tabular-nums ${
        // red-600, not red-500: white 10px text on red-500 is 3.76:1, under AA; red-600 is 4.83:1.
        badge.kind === 'error' ? 'bg-[var(--bg-overlay)] text-amber-400 border border-amber-500' : 'bg-red-600 text-white'
      }`}
    >
      {badge.kind === 'error' ? '!' : badge.text}
    </span>
  );
}

interface AppRailProps {
  sidebarOpen: boolean;
  onToggleSidebar: () => void;
  /** Opens the existing page search (expands the sidebar if needed, then focuses it). */
  onSearch: () => void;
  /** Extra account-menu content — the admin view picker (SPEC-515, the only one) lives here. */
  accountExtras?: React.ReactNode;
}

export function AppRail({ sidebarOpen, onToggleSidebar, onSearch, accountExtras }: AppRailProps) {
  const { user, logout, viewPreview } = useAuth();
  const { workspace } = useWorkspace();
  const { count, error, openDesk, refresh } = useRulings();
  const { openPicker } = useTour();
  const location = useLocation();

  const rolePages = user?.pages || ['/'];
  const pages = workspace?.pages
    ? rolePages.filter((p: string) => workspace.pages!.includes(p))
    : rolePages;
  const items = railItems(user?.role, pages);
  const footItems = railFootItems(pages);
  const homeLink = brandLinksHome(pages);
  const badge = decisionsBadge(count, error);
  const adminArea = location.pathname.startsWith('/admin') || location.pathname.startsWith('/super-admin');
  // SPEC-535: the Trust button lights on any page of the Trust group (config/nav-groups).
  const trustArea = NAV_GROUP_PAGES.Trust.some((p) => location.pathname.startsWith(p));

  // SPEC-466 + SPEC-509: the two feeds behind the ONE Alerts badge — critical findings in
  // this login's remit and unread updates (the old top-bar bell). Never summed; a failed
  // read is "!", never a fake zero. Not polled when the login has no Alerts page.
  const feeds = useAlertsFeeds(footItems.includes('alerts'));
  const aBadge = alertsBadge(feeds);
  const aLabel = alertsLabel(feeds);

  const decisionsLabel = error
    ? 'Decisions — could not load, open to retry'
    : count > 0
      ? `Decisions — ${count} waiting for you`
      : 'Decisions — nothing waiting';

  const navClass = ({ isActive }: { isActive: boolean }) => `group ${ITEM_BASE} ${isActive ? ITEM_ACTIVE : ITEM_IDLE}`;

  const render = (id: RailItemId) => {
    switch (id) {
      case 'trust':
        // SPEC-535 (Param, 08 Oct): was "Methodology". It now opens the Trust group at its first
        // page, Docs; the sidebar's follow-the-active-page rule opens the Trust group there.
        // Active on every Trust page, not only /docs, so the button says where you are.
        return (
          // A plain Link, not NavLink: NavLink applies its `aria-current` prop only while ITS
          // path matches, so on /methodology the explicit value below would be dropped.
          // 'page' on Docs itself; 'true' (current item of the set) on the other Trust pages.
          <Link
            key={id}
            to="/docs"
            aria-label="Trust — docs, methods and data"
            aria-current={trustArea ? (location.pathname === '/docs' ? 'page' : 'true') : undefined}
            className={`group ${ITEM_BASE} ${trustArea ? ITEM_ACTIVE : ITEM_IDLE}`}
          >
            <RailMark id="trust" />
            <Tip text="Trust" />
          </Link>
        );
      case 'help':
        // SPEC-503: opens the Docs page at the guide for THIS screen and tab (config/docs.ts
        // resolves the view id; a screen with no guide lands on its catalogue entry).
        return (
          <NavLink
            key={id}
            to={helpHrefFor(location.pathname, location.search)}
            aria-label="Help for this page"
            data-tour="docs-help"
            className={navClass}
          >
            <RailMark id="help" />
            <Tip text="Help for this page" />
          </NavLink>
        );
      case 'admin':
        // Active on /admin and /super-admin alike — both are the admin area. aria-current is
        // set explicitly because NavLink's own matching knows only /admin.
        return (
          <NavLink
            key={id}
            to="/admin"
            aria-label="Admin"
            aria-current={adminArea ? 'page' : undefined}
            className={`group ${ITEM_BASE} ${adminArea ? ITEM_ACTIVE : ITEM_IDLE}`}
          >
            <RailMark id="admin" />
            <Tip text="Admin" />
          </NavLink>
        );
      case 'search':
        return (
          <button
            key={id}
            type="button"
            onClick={onSearch}
            aria-label="Search pages"
            // No tour anchor here: the "nav-search" step says "type here" and stays on the
            // sidebar's search box; the rail as a whole is anchored as "app-rail".
            className={`group ${ITEM_BASE} ${ITEM_IDLE}`}
          >
            <RailMark id="search" />
            <Tip text="Search pages" />
          </button>
        );
      case 'decisions':
        return (
          <button
            key={id}
            type="button"
            // A failed load is retried on open, as the "!" label promises; the desk itself
            // shows the failure (never "nothing waiting") until a load succeeds.
            onClick={() => { if (error) refresh(); openDesk(); }}
            aria-label={decisionsLabel}
            // SPEC-145: the Decisions-desk tour step's anchor moved from the old top-bar
            // pill to here (the key is kept so the tour data and its tests stay stable).
            data-tour="rulings-pill"
            className={`group ${ITEM_BASE} ${count > 0 && !error ? ITEM_LIT : ITEM_IDLE}`}
          >
            <RailMark id="decisions" />
            {badge && (
              <span
                aria-hidden="true"
                className={`absolute top-0.5 right-0.5 min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold leading-4 text-center tabular-nums ${
                  error ? 'bg-[var(--bg-overlay)] text-amber-400 border border-amber-500' : 'bg-amber-500 text-[var(--text-inverse)]'
                }`}
              >
                {badge}
              </span>
            )}
            <Tip text={decisionsLabel} />
          </button>
        );
      default:
        return null;
    }
  };

  const renderFoot = (id: RailFootItemId) => {
    switch (id) {
      case 'alerts': {
        const waiting = aBadge?.kind === 'count' || aBadge?.kind === 'dot';
        return (
          <NavLink
            key={id}
            to="/alerts"
            aria-label={aLabel}
            data-tour="rail-alerts"
            className={({ isActive }) => `group ${ITEM_BASE} ${isActive ? ITEM_ACTIVE : waiting ? ITEM_LIT : ITEM_IDLE}`}
          >
            <RailMark id="alerts" />
            <AlertsBadgeMark badge={aBadge} />
            <Tip text={aLabel} />
          </NavLink>
        );
      }
      case 'tours':
        // Opens the existing walk-through picker (the top-bar button it replaces did the same).
        return (
          <button
            key={id}
            type="button"
            onClick={openPicker}
            aria-label="Guided walk-throughs"
            aria-haspopup="dialog"
            data-tour="rail-tours"
            className={`group ${ITEM_BASE} ${ITEM_IDLE}`}
          >
            <RailMark id="tours" />
            <Tip text="Guided walk-throughs" />
          </button>
        );
      case 'feedback':
        // SPEC-506 owns the Feedback panel and listens for this event. The button fires it
        // and does nothing else (harmless before 506 lands).
        return (
          <button
            key={id}
            type="button"
            onClick={() => openFeedback(window, 'rail')}
            aria-label="Feedback"
            data-tour="rail-feedback"
            className={`group ${ITEM_BASE} ${ITEM_IDLE}`}
          >
            <RailMark id="feedback" />
            <Tip text="Feedback" />
          </button>
        );
      default:
        return null;
    }
  };

  return (
    <nav
      aria-label="Global"
      data-tour="app-rail"
      className="flex flex-col items-center w-[52px] shrink-0 h-full bg-[var(--sidebar-bg)] border-r border-[var(--border-subtle)] py-3 gap-1"
    >
      {/* SPEC-509: the brand mark IS the way home — a link to Overview, named "Overview"
          (it replaced the separate Home button). It stays in full brand colour at rest; on
          Overview it gets the active tint plus an amber ring and aria-current="page". A
          login without Overview keeps the plain, decorative mark (never a dead link). */}
      {homeLink ? (
        <NavLink
          to="/"
          end
          aria-label="Overview"
          className={({ isActive }) => `group ${ITEM_BASE} mb-1 text-[var(--text-primary)] ${
            isActive ? 'bg-[var(--sidebar-active-bg)] ring-[1.5px] ring-amber-400' : 'hover:bg-[var(--sidebar-hover)]'
          }`}
        >
          <SuryaMark size={26} />
          <Tip text="Overview" />
        </NavLink>
      ) : (
        <div className="grid place-items-center w-10 h-10 mb-1 text-[var(--text-primary)]">
          <SuryaMark size={26} />
        </div>
      )}

      <button
        type="button"
        onClick={onToggleSidebar}
        // A STABLE name; aria-expanded carries the state (a label that also flipped would
        // be announced twice). The visual tooltip says what a click will do.
        aria-label="Menu"
        aria-expanded={sidebarOpen}
        aria-controls="app-sidebar"
        className={`group ${ITEM_BASE} ${ITEM_IDLE}`}
      >
        <RailMark id="menu" />
        <Tip text={sidebarOpen ? 'Hide menu' : 'Show menu'} />
      </button>

      <div className="w-6 border-t border-[var(--border-subtle)] my-1" aria-hidden="true" />

      {items.map(render)}

      <div className="flex-1" />

      {/* SPEC-509: the foot cluster — Alerts, Walk-throughs, Feedback — above Account. */}
      {footItems.map(renderFoot)}

      <div className="w-6 border-t border-[var(--border-subtle)] my-1" aria-hidden="true" />

      <AccountMenu
        name={user?.full_name || user?.username || 'Account'}
        previewName={viewPreview ? (viewPreview.label || viewPreview.name) : undefined}
        onSignOut={logout}
        extras={accountExtras}
      />
    </nav>
  );
}

/** The account control at the foot of the rail: who is signed in, the admin view picker
 *  (via `extras`), Arrange menu (SPEC-563), Change password (SPEC-489), Sign out of all devices
 *  (SPEC-566) and Sign out. Sign out sits apart from the navigation items
 *  (destructive-nav separation). A disclosure, not an ARIA menu: the trigger has
 *  aria-expanded + aria-controls and the panel is a labelled group. Escape closes it and
 *  returns focus to the trigger; an outside click or focus leaving it closes it too. */
function AccountMenu({
  name,
  previewName,
  onSignOut,
  extras,
}: {
  name: string;
  previewName?: string;
  onSignOut: () => void;
  extras?: React.ReactNode;
}) {
  const [open, setOpen] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  // SPEC-563 (D1): "Arrange menu" opens the shared Arrange dialog (ArrangeMenuHost in AppShell).
  const { openArrange } = useNavLayout();
  const [signingOutAll, setSigningOutAll] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    // Move focus into the panel so a keyboard user lands on its first control.
    menuRef.current?.querySelector<HTMLElement>('select, button')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const label = previewName ? `Account — ${name} (previewing the "${previewName}" view)` : `Account — ${name}`;

  // SPEC-566: ends every sign-in of this account, this device included, then signs out here.
  async function signOutEverywhere() {
    const ok = window.confirm(
      'Sign out on every device, this one included? Use this if you left yourself signed in somewhere else.',
    );
    if (!ok) return;
    setSigningOutAll(true);
    try {
      await request('/api/auth/sessions/revoke-all', { method: 'POST' });
    } catch {
      setSigningOutAll(false);
      window.alert('Could not sign out of all devices. Try again, or ask your administrator.');
      return;
    }
    onSignOut();
  }

  return (
    <div
      className="relative"
      ref={wrapRef}
      // Tabbing out of the panel closes it, so it never sits open over the sidebar.
      onBlur={(e) => {
        // Only when focus MOVES to a known element outside. Safari/Firefox on macOS do not
        // focus a button on mousedown, so a click inside the panel blurs with a NULL
        // relatedTarget — closing on null would unmount the panel before Sign out's click
        // lands (delta review finding 1). Outside clicks are the mousedown listener's job.
        const next = e.relatedTarget as Node | null;
        if (open && next && !wrapRef.current?.contains(next)) setOpen(false);
      }}
    >
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={label}
        aria-expanded={open}
        aria-controls="rail-account-panel"
        data-tour="rail-account"
        className={`group ${ITEM_BASE} ${
          previewName ? 'text-amber-400 [--mark-accent:var(--amber-400)] ring-1 ring-amber-500/60 hover:bg-[var(--sidebar-hover)]' : ITEM_IDLE
        }`}
      >
        <RailMark id="account" />
        {!open && <Tip text={previewName ? `${name} · previewing "${previewName}"` : name} />}
      </button>
      {open && (
        <div
          ref={menuRef}
          id="rail-account-panel"
          role="group"
          aria-label="Account"
          className="absolute left-full bottom-0 ml-2 z-50 w-64 rounded-xl border border-[var(--border-default)] bg-[var(--bg-raised)] shadow-2xl p-3 space-y-3"
        >
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)] truncate">{name}</p>
            <p className="text-[11px] text-[var(--text-tertiary)]">Flying Trade Group</p>
          </div>
          {extras}
          <div className="border-t border-[var(--border-subtle)] pt-2 space-y-1">
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                openArrange();
              }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
            >
              <ListTree className="w-4 h-4" aria-hidden="true" />
              Arrange menu
            </button>
            <button
              type="button"
              onClick={() => {
                setOpen(false);
                setChangingPassword(true);
              }}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
            >
              <KeyRound className="w-4 h-4" aria-hidden="true" />
              Change password
            </button>
            <button
              type="button"
              onClick={signOutEverywhere}
              disabled={signingOutAll}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-red-400 hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 disabled:cursor-wait disabled:opacity-60 transition-colors"
            >
              <MonitorOff className="w-4 h-4" aria-hidden="true" />
              {signingOutAll ? 'Signing out…' : 'Sign out of all devices'}
            </button>
            <button
              type="button"
              onClick={onSignOut}
              className="w-full flex items-center gap-2 px-2 py-2 rounded-lg text-sm text-[var(--text-secondary)] hover:text-red-400 hover:bg-[var(--bg-overlay)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-amber-500/60 transition-colors"
            >
              <LogOut className="w-4 h-4" aria-hidden="true" />
              Sign out
            </button>
          </div>
        </div>
      )}
      {changingPassword && (
        <ChangePasswordDialog
          onClose={() => {
            setChangingPassword(false);
            triggerRef.current?.focus();
          }}
        />
      )}
    </div>
  );
}
